import { apiRequest } from '@/integrations/api/client';
import { setAccessToken } from '@/integrations/api/token_storage';

export type AppRole = 'owner' | 'admin' | 'supervisor' | 'operator';

export interface ApiUser {
  id: string;
  uuid: string;
  email: string;
  full_name: string | null;
  profile: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    department: string | null;
    shift: string | null;
  } | null;
  role: AppRole | null;
  tenant: { id: string; slug: string; name: string } | null;
  mfa_enrolled: boolean;
}

export interface AuthTokenResponse {
  type: string;
  value: string;
  expiresAt: string | null;
}

export type LoginResult =
  | { mfa_required: true; mfa_ticket: string; message?: string }
  | { user: ApiUser; token: AuthTokenResponse };

function storeToken(token: AuthTokenResponse) {
  setAccessToken(token.value, token.expiresAt);
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const res = await apiRequest<LoginResult & { message?: string }>('/api/auth/login/users', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: { email, password },
  });
  if ('mfa_required' in res && res.mfa_required) {
    return { mfa_required: true, mfa_ticket: res.mfa_ticket, message: res.message };
  }
  const ok = res as { user: ApiUser; token: AuthTokenResponse };
  storeToken(ok.token);
  return ok;
}

export async function completeMfaLogin(input: {
  mfa_ticket: string;
  code?: string;
  backup_code?: string;
  trust_device?: boolean;
  device_label?: string;
}) {
  const res = await apiRequest<{ user: ApiUser; token: AuthTokenResponse }>(
    '/api/auth/login/mfa/users',
    {
      method: 'POST',
      auth: false,
      skipRefresh: true,
      body: input,
    },
  );
  storeToken(res.token);
  return res;
}

export async function register(input: {
  email: string;
  password: string;
  password_confirmation: string;
  full_name?: string;
  invitation_token?: string;
}) {
  const res = await apiRequest<{
    user: ApiUser;
    token: AuthTokenResponse;
  }>('/api/auth/register/users', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: input,
  });
  storeToken(res.token);
  return res;
}

export async function fetchMe() {
  return apiRequest<{ user: ApiUser }>('/api/auth/me/users');
}

export async function refreshToken() {
  const res = await apiRequest<{ token: AuthTokenResponse }>('/api/auth/refresh/users', {
    method: 'POST',
    skipRefresh: true,
  });
  storeToken(res.token);
  return res;
}

export async function logout() {
  try {
    await apiRequest('/api/auth/logout/users', { method: 'POST' });
  } finally {
    setAccessToken(null);
  }
}

export async function logoutAll() {
  return apiRequest<{ message: string; tokens_revoked: number }>('/api/auth/logout-all/users', {
    method: 'POST',
  });
}

export async function updatePassword(input: {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}) {
  return apiRequest('/api/auth/password/update/users', {
    method: 'POST',
    body: input,
  });
}

export async function listLoginEvents() {
  return apiRequest<{
    events: Array<{
      id: string;
      ip: string | null;
      user_agent: string | null;
      geo_country: string | null;
      geo_city: string | null;
      mfa_used: boolean;
      created_at: string;
    }>;
  }>('/api/auth/login-events');
}

export async function listTrustedDevices() {
  return apiRequest<{
    devices: Array<{
      id: string;
      device_label: string | null;
      last_seen_at: string;
      expires_at: string;
      revoked_at: string | null;
      created_at: string;
    }>;
  }>('/api/auth/trusted-devices');
}

export async function revokeTrustedDevice(id: string) {
  return apiRequest(`/api/auth/trusted-devices/${id}`, { method: 'DELETE' });
}

export async function revokeOtherTrustedDevices() {
  return apiRequest<{ revoked: number }>('/api/auth/trusted-devices/revoke-others', {
    method: 'POST',
  });
}

export async function requestPasswordReset(email: string) {
  return apiRequest('/api/auth/forgot-password/users', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: { email },
  });
}

export async function resetPassword(input: {
  token: string;
  email: string;
  new_password: string;
  new_password_confirmation: string;
}) {
  return apiRequest('/api/auth/reset-password/users', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: input,
  });
}

export async function listMfaFactors() {
  return apiRequest<{ totp: Array<{ id: string; friendly_name: string | null; status: string }> }>(
    '/api/auth/mfa',
  );
}

export async function enrollMfa(friendlyName?: string) {
  return apiRequest<{ id: string; totp: { qr_code: string; secret: string } }>('/api/auth/mfa/enroll', {
    method: 'POST',
    body: { friendly_name: friendlyName },
  });
}

export async function verifyMfa(factorId: string, code: string) {
  return apiRequest('/api/auth/mfa/verify', {
    method: 'POST',
    body: { factor_id: factorId, code },
  });
}

export async function unenrollMfa(factorId?: string) {
  return apiRequest('/api/auth/mfa', {
    method: 'DELETE',
    body: factorId ? { factor_id: factorId } : {},
  });
}

export async function generateMfaBackupCodes() {
  return apiRequest<{ backup_codes: string[] }>('/api/auth/mfa/backup-codes', {
    method: 'POST',
  });
}
